import { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from "../ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../ui/command";
import { User, Loader2, Check, Camera, ImageIcon, Globe, Trash2, ChevronsUpDown, MapPin, X, SwitchCamera, Copy, Gift, Share2 } from "lucide-react";
import { userApi, contentApi } from "../../lib/api";
import { toast } from "sonner";
import AvatarCropModal from "../../pages/AvatarCropModal";
import UserAvatar from "../UserAvatar";
import { captureError } from "../../lib/sentry";

const CameraModal = ({ onCapture, onClose }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [facingMode, setFacingMode] = useState("user");
  const [cameraCount, setCameraCount] = useState(1);

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        stopStream();
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: facingMode },
            width: { ideal: 1280 },
            height: { ideal: 960 },
          },
        });
        if (cancelled) { stream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
        const devices = await navigator.mediaDevices.enumerateDevices();
        if (!cancelled) {
          setCameraCount(devices.filter((device) => device.kind === "videoinput").length);
        }
      } catch {
        toast.error("Could not access camera. Please check permissions.");
        onClose();
      }
    })();
    return () => { cancelled = true; stopStream(); };
  }, [facingMode, onClose, stopStream]);

  const handleCapture = () => {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
    stopStream();
    onCapture(dataUrl);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="camera-modal">
      <div className="bg-background rounded-2xl overflow-hidden shadow-xl max-w-md w-full">
        <div className="relative aspect-[4/3] bg-black">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className={`w-full h-full object-cover ${facingMode === "user" ? "-scale-x-100" : ""}`}
          />
          {cameraCount > 1 && (
            <Button
              type="button"
              size="icon"
              variant="secondary"
              className="absolute right-3 top-3 rounded-full bg-black/55 text-white hover:bg-black/70"
              onClick={() => setFacingMode((current) => current === "user" ? "environment" : "user")}
              title="Switch camera"
              aria-label="Switch camera"
              data-testid="camera-switch-btn"
            >
              <SwitchCamera className="w-5 h-5" />
            </Button>
          )}
        </div>
        <div className="flex justify-center gap-3 p-4">
          <Button variant="outline" onClick={onClose} className="rounded-xl">Cancel</Button>
          <Button onClick={handleCapture} className="rounded-xl gap-2" data-testid="camera-capture-btn">
            <Camera className="w-4 h-4" /> Take Photo
          </Button>
        </div>
      </div>
    </div>
  );
};

const COUNTRIES = [
  "Afghanistan","Albania","Algeria","Andorra","Angola","Argentina","Armenia","Australia",
  "Austria","Azerbaijan","Bahamas","Bahrain","Bangladesh","Barbados","Belarus","Belgium",
  "Belize","Benin","Bhutan","Bolivia","Bosnia and Herzegovina","Botswana","Brazil","Brunei",
  "Bulgaria","Burkina Faso","Burundi","Cambodia","Cameroon","Canada","Central African Republic",
  "Chad","Chile","China","Colombia","Comoros","Congo","Costa Rica","Croatia","Cuba","Cyprus",
  "Czech Republic","Denmark","Djibouti","Dominican Republic","Ecuador","Egypt","El Salvador",
  "Estonia","Ethiopia","Fiji","Finland","France","Gabon","Gambia","Georgia","Germany","Ghana",
  "Greece","Guatemala","Guinea","Haiti","Honduras","Hungary","Iceland","India","Indonesia",
  "Iran","Iraq","Ireland","Israel","Italy","Jamaica","Japan","Jordan","Kazakhstan","Kenya",
  "Kuwait","Kyrgyzstan","Laos","Latvia","Lebanon","Libya","Liechtenstein","Lithuania",
  "Luxembourg","Madagascar","Malaysia","Maldives","Mali","Malta","Mexico","Moldova","Monaco",
  "Mongolia","Montenegro","Morocco","Mozambique","Myanmar","Namibia","Nepal","Netherlands",
  "New Zealand","Nicaragua","Niger","Nigeria","North Macedonia","Norway","Oman","Pakistan",
  "Panama","Paraguay","Peru","Philippines","Poland","Portugal","Qatar","Romania","Russia",
  "Rwanda","Saudi Arabia","Senegal","Serbia","Singapore","Slovakia","Slovenia","Somalia",
  "South Africa","South Korea","Spain","Sri Lanka","Sudan","Sweden","Switzerland","Syria",
  "Taiwan","Tanzania","Thailand","Togo","Trinidad and Tobago","Tunisia","Turkey","Uganda",
  "Ukraine","United Arab Emirates","United Kingdom","United States","Uruguay","Uzbekistan",
  "Venezuela","Vietnam","Yemen","Zambia","Zimbabwe"
];

const CountryCombobox = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const filtered = search
    ? COUNTRIES.filter(c => c.toLowerCase().includes(search.toLowerCase()))
    : COUNTRIES;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full h-11 text-sm justify-between font-normal"
          data-testid="profile-country-select"
        >
          {value || "Select your country"}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Type to search..." value={search} onValueChange={setSearch} />
          <CommandList className="max-h-48">
            <CommandEmpty>No country found.</CommandEmpty>
            <CommandGroup>
              {filtered.map((c) => (
                <CommandItem key={c} value={c} onSelect={() => { onChange(c); setOpen(false); setSearch(""); }}>
                  <Check className={`mr-2 h-4 w-4 ${value === c ? "opacity-100" : "opacity-0"}`} />
                  {c}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

const HomeCityInput = ({ value, onChange }) => {
  const [query, setQuery] = useState(value?.name || "");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => { setQuery(value?.name || ""); }, [value]);

  useEffect(() => {
    if (!query || query.length < 2 || (value && query === value.name)) {
      setSuggestions([]);
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await contentApi.searchAutocomplete(query, null, null, {
          types: "locality,administrative_area_level_3,postal_town",
        });
        const data = res.data || {};
        setSuggestions(data.suggestions || []);
        setOpen((data.suggestions || []).length > 0);
      } catch { setSuggestions([]); }
      finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [query, value]);

  const handleSelect = async (suggestion) => {
    setOpen(false);
    setQuery(suggestion.description || suggestion.name);
    setSuggestions([]);
    try {
      const res = await contentApi.getPlaceDetails(suggestion.place_id);
      const data = res.data || {};
      if (data.latitude && data.longitude) {
        onChange({ name: suggestion.description || suggestion.name, lat: data.latitude, lng: data.longitude });
      }
    } catch {
      onChange({ name: suggestion.description || suggestion.name, lat: null, lng: null });
    }
  };

  const handleClear = () => { setQuery(""); setSuggestions([]); onChange(null); };

  return (
    <div className="relative">
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Start typing your city..."
          value={query}
          onChange={(e) => { setQuery(e.target.value); if (value) onChange(null); }}
          onFocus={() => { if (suggestions.length > 0) setOpen(true); }}
          className="h-11 text-sm pl-9 pr-9"
          autoComplete="off"
          name="home-city-search"
          data-testid="profile-homecity-input"
        />
        {(value || query) && (
          <button type="button" onClick={handleClear} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      {open && suggestions.length > 0 && (
        <div className="absolute z-50 mt-1 w-full bg-popover border rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {suggestions.map((s) => (
            <button key={s.place_id} type="button"
              className="w-full text-left px-3 py-2.5 text-sm hover:bg-accent transition-colors first:rounded-t-lg last:rounded-b-lg"
              onClick={() => handleSelect(s)}
            >
              {s.description}
            </button>
          ))}
        </div>
      )}
      {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />}
    </div>
  );
};

const ProfileSection = ({ user, updateUser }) => {
  const [name, setName] = useState(user?.name || "");
  const [country, setCountry] = useState(user?.country || "");
  const [homeCity, setHomeCity] = useState(
    user?.home_city ? { name: user.home_city, lat: user.home_city_lat, lng: user.home_city_lng } : null
  );
  const [saving, setSaving] = useState(false);
  const [cropImage, setCropImage] = useState(null);
  const [avatarSaving, setAvatarSaving] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [languages, setLanguages] = useState([]);
  const [audioLang, setAudioLang] = useState(user?.default_audio_language || "en");
  const [referral, setReferral] = useState(null);
  const [referralLoading, setReferralLoading] = useState(true);
  const [langSaving, setLangSaving] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (user) {
      setName(user.name || "");
      setCountry(user.country || "");
      setHomeCity(user.home_city ? { name: user.home_city, lat: user.home_city_lat, lng: user.home_city_lng } : null);
      setAudioLang(user.default_audio_language || "en");
    }
  }, [user]);

  useEffect(() => {
    contentApi.getLanguages().then(r => setLanguages(r.data.languages || [])).catch((error) => {
      captureError(error, { action: "load_profile_languages" });
    });
  }, []);

  useEffect(() => {
    let active = true;
    userApi.getReferral()
      .then((response) => { if (active) setReferral(response.data); })
      .catch((error) => captureError(error, { action: "load_referral" }))
      .finally(() => { if (active) setReferralLoading(false); });
    return () => { active = false; };
  }, []);

  const referralUrl = referral?.code
    ? `${window.location.origin}/signup?ref=${encodeURIComponent(referral.code)}`
    : "";

  const copyReferral = async () => {
    if (!referralUrl) return;
    try {
      await navigator.clipboard.writeText(referralUrl);
      toast.success("Referral link copied");
    } catch {
      toast.error("Could not copy referral link");
    }
  };

  const copyReferralCode = async () => {
    if (!referral?.code) return;
    try {
      await navigator.clipboard.writeText(referral.code);
      toast.success("Referral code copied");
    } catch {
      toast.error("Could not copy referral code");
    }
  };

  const shareReferral = async () => {
    if (!referralUrl) return;
    const text = "Join me on Culture Ken. You get 5 credits when you sign up and I get 2 credits when you download your first audio guide!";
    if (navigator.share) {
      try {
        await navigator.share({ title: "Culture Ken", text, url: referralUrl });
        return;
      } catch (error) {
        if (error?.name === "AbortError") return;
      }
    }
    await copyReferral();
  };

  const hasChanged =
    name.trim() !== (user?.name || "") ||
    country !== (user?.country || "") ||
    (homeCity?.name || "") !== (user?.home_city || "");

  const handleSave = async () => {
    if (!name.trim()) return toast.error("Name cannot be empty");
    setSaving(true);
    try {
      const payload = { name: name.trim(), country };
      if (homeCity) {
        payload.home_city = homeCity.name;
        payload.home_city_lat = homeCity.lat;
        payload.home_city_lng = homeCity.lng;
      } else {
        payload.home_city = "";
        payload.home_city_lat = null;
        payload.home_city_lng = null;
      }
      await userApi.updateProfile(payload);
      updateUser({
        name: name.trim(),
        country,
        home_city: payload.home_city,
        home_city_lat: payload.home_city_lat,
        home_city_lng: payload.home_city_lng,
      });
      toast.success("Profile updated");
    } catch (error) {
      if (!error.response || error.response.status >= 500) {
        captureError(error, { action: "update_profile" });
      }
      toast.error("Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const handleLangChange = async (newLang) => {
    setAudioLang(newLang);
    setLangSaving(true);
    try {
      await userApi.updatePreferences({ default_audio_language: newLang });
      updateUser({ default_audio_language: newLang });
      toast.success("Default audio language updated");
    } catch (error) {
      if (!error.response || error.response.status >= 500) {
        captureError(error, { action: "update_default_audio_language", language: newLang });
      }
      toast.error("Failed to update language");
    } finally {
      setLangSaving(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please select an image"); return; }
    const reader = new FileReader();
    reader.onload = () => setCropImage(reader.result);
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const handleCropSave = async (blob) => {
    setAvatarSaving(true);
    try {
      const res = await userApi.uploadAvatar(blob);
      updateUser({ avatar_url: res.data.avatar_url, avatar_cache_bust: Date.now() });
      toast.success("Photo updated!");
      setCropImage(null);
    } catch (error) {
      captureError(error, { action: "upload_avatar" });
      toast.error("Failed to upload photo");
    }
    finally { setAvatarSaving(false); }
  };

  const handleRemoveAvatar = async () => {
    try {
      await userApi.updateProfile({ name: name.trim() || user?.name || "User" });
      await userApi.uploadAvatar(null);
    } catch { /* ignore */ }
    updateUser({ avatar_url: "", avatar_cache_bust: Date.now() });
    toast.success("Profile picture removed");
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <User className="w-5 h-5 text-primary" />
            <CardTitle className="text-lg">Profile</CardTitle>
          </div>
          <CardDescription>{user?.email}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <form autoComplete="off" onSubmit={(e) => e.preventDefault()} className="space-y-6">
          {/* Avatar with dropdown */}
          <div className="flex flex-col items-center gap-2">
            <div className="relative group" data-testid="avatar-display">
              <UserAvatar user={user} size="md" />
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="text-sm text-primary hover:underline cursor-pointer"
                  data-testid="change-profile-picture-btn"
                >
                  Change profile picture
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center">
                <DropdownMenuItem onClick={() => fileInputRef.current?.click()} data-testid="avatar-choose-photo-btn">
                  <ImageIcon className="w-4 h-4 mr-2" />
                  Choose photo
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setShowCamera(true)} data-testid="avatar-take-photo-btn">
                  <Camera className="w-4 h-4 mr-2" />
                  Take photo
                </DropdownMenuItem>
                {user?.avatar_url && (
                  <DropdownMenuItem onClick={handleRemoveAvatar} className="text-destructive focus:text-destructive" data-testid="avatar-remove-btn">
                    <Trash2 className="w-4 h-4 mr-2" />
                    Remove photo
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileSelect} data-testid="avatar-file-input" />
          </div>

          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="display-name" className="text-sm font-medium">Display Name</Label>
            <Input id="display-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="h-11" autoComplete="off" data-testid="profile-name-input" />
          </div>

          {/* Credits (read-only) */}
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Credits</span>
            <span className="font-semibold">{user?.credits || 0}</span>
          </div>

          <div className="space-y-3 rounded-lg border border-border bg-muted/30 p-4" data-testid="profile-referral-card">
            <div className="flex items-start gap-3">
              <Gift className="mt-0.5 h-5 w-5 text-primary" />
              <div>
                <p className="font-semibold text-foreground">Invite friends</p>
                <p className="text-xs text-muted-foreground">
                  You get 2 free credits when they download their first audio guide!
                </p>
              </div>
            </div>
            {referralLoading ? (
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
            ) : referralUrl ? (
              <>
                <div className="rounded-md border border-input bg-background px-3 py-2">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <p className="text-xs text-muted-foreground">Your code</p>
                      <p className="font-mono text-base font-semibold" data-testid="profile-referral-code">
                        {referral.code}
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={copyReferralCode}
                      title="Copy referral code"
                      aria-label="Copy referral code"
                      data-testid="copy-referral-code-btn"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="mt-1 truncate text-xs text-muted-foreground" title={referralUrl}>
                    {referralUrl}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" className="flex-1" onClick={copyReferral} data-testid="copy-referral-link-btn">
                    <Copy className="mr-2 h-4 w-4" /> Copy link
                  </Button>
                  <Button type="button" className="flex-1" onClick={shareReferral} data-testid="share-referral-link-btn">
                    <Share2 className="mr-2 h-4 w-4" /> Share
                  </Button>
                </div>
              </>
            ) : null}
          </div>

          {/* Country */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Country</Label>
            <CountryCombobox value={country} onChange={setCountry} />
          </div>

          {/* Home City */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Home town / city</Label>
            <HomeCityInput value={homeCity} onChange={setHomeCity} />
          </div>

          {/* Default Audio Language */}
          <div className="space-y-2">
            <Label className="text-sm font-medium flex items-center gap-1.5">
              <Globe className="w-4 h-4 text-muted-foreground" />
              Default Audio Language
            </Label>
            <select
              value={audioLang}
              onChange={(e) => handleLangChange(e.target.value)}
              disabled={langSaving}
              autoComplete="off"
              className="w-full h-11 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              data-testid="default-audio-language-select"
            >
              {languages.length > 0 ? languages.map(l => (
                <option key={l.code} value={l.code}>{l.label}</option>
              )) : (
                <option value="en">English</option>
              )}
            </select>
            <p className="text-xs text-muted-foreground">New audio guides will be generated in this language</p>
          </div>

          {/* Save button */}
          {hasChanged && (
            <Button onClick={handleSave} disabled={saving} className="w-full h-10 rounded-xl" data-testid="save-profile-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
          )}
          </form>
        </CardContent>
      </Card>

      {showCamera && (
        <CameraModal
          onCapture={(dataUrl) => { setShowCamera(false); setCropImage(dataUrl); }}
          onClose={() => setShowCamera(false)}
        />
      )}

      {cropImage && (
        <AvatarCropModal imageSrc={cropImage} onSave={handleCropSave} onCancel={() => setCropImage(null)} saving={avatarSaving} />
      )}
    </>
  );
};

export default ProfileSection;
